import { useEffect, useRef } from "react";
import { invalidate } from "@react-three/fiber";
import * as THREE from "three";
import { Mesh } from 'three';
import { renderStore } from "../../store/renderStore";
import { observer } from "mobx-react-lite";

/** Font size the canvas supersampling is calibrated for (roughly zoom level 1). */
const REFERENCE_FONT_SIZE = 20;
/** Guards against division blow-ups for degenerate font sizes. */
const MIN_FONT_SIZE = 0.25;
/** Upper bound on the extra supersampling applied while zoomed in. */
const MAX_DENSITY_SCALE = 32;
/** Hard cap on the label canvas so deep zoom never allocates a huge texture. */
const MAX_CANVAS_DIMENSION = 2048;

interface SimpleTextProps {
    children: string;
    fontSize?: number;
    fontWeight?: string | number;
    color?: string;
    position?: [number, number, number];
    padding?: number; // Optional padding around the text
    rotation?: [number, number, number]; // Rotation angles in radians [x, y, z]
    renderOrder?: number;
    depthTest?: boolean;
    raycast?: THREE.Object3D["raycast"];
}

export const SimpleText: React.FC<SimpleTextProps> = observer(({
    children,
    fontSize = 18,
    fontWeight = 'normal',
    color = 'black',
    position = [0, 0, 0],
    padding = 10, // Default padding of 10 pixels
    rotation = [0, 0, 0], // Default rotation of [0, 0, 0] (no rotation)
    renderOrder = 2,
    depthTest = false,
    raycast,
}) => {
    const textMesh = useRef<Mesh>(null);
    const textureRef = useRef<THREE.CanvasTexture | null>(null);
    const font = `${fontWeight} ${fontSize}px sans-serif`;

    useEffect(() => {
        // Increase scale factor when rendering for export to ensure crisp text
        // Base scale is 4x, increase significantly for exports to match 2x render resolution
        // This ensures text remains sharp even at high export resolutions and large floorplans
        const baseScale = 4;
        const exportScaleMultiplier = renderStore.rendering ? 4 : 1; // 4x multiplier for exports (16x total)
        // Callers shrink fontSize as the camera zooms in so the label keeps a
        // constant on-screen size. Rasterizing at that shrinking size collapses
        // the texture resolution and the label turns blurry when zoomed in, so
        // raise the supersampling by the same factor to keep the texel density
        // (and therefore the sharpness) constant at every zoom level.
        const densityScale = THREE.MathUtils.clamp(
            REFERENCE_FONT_SIZE / Math.max(fontSize, MIN_FONT_SIZE),
            1,
            MAX_DENSITY_SCALE,
        );
        const scale = baseScale * exportScaleMultiplier * densityScale;
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) return;

        context.font = font;

        // Measure text width and estimate height
        const textMetrics = context.measureText(children);
        const textWidth = textMetrics.width;
        const textHeight = fontSize * 1.2; // Estimate text height with line spacing

        // Add padding to the canvas size, capping the supersampling so a single
        // label can never allocate an oversized canvas at extreme zoom levels.
        const planeWidth = textWidth + padding * 2;
        const planeHeight = textHeight + padding * 2;
        const cappedScale = Math.min(
            scale,
            MAX_CANVAS_DIMENSION / Math.max(planeWidth, planeHeight, 1),
        );
        const canvasWidth = planeWidth * cappedScale;
        const canvasHeight = planeHeight * cappedScale;

        // Set canvas size and scale
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        context.scale(cappedScale, cappedScale); // Apply scaling to draw text at high resolution

        // Clear the canvas and set the font properties
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.font = font;
        context.fillStyle = color;
        context.textAlign = 'center';
        context.textBaseline = 'middle';

        // Draw the text centered with padding
        context.fillText(
            children,
            planeWidth / 2,
            planeHeight / 2
        );

        // Calculate plane size synchronously
        const newPlaneSize: [number, number] = [planeWidth / 100, planeHeight / 100];

        // Validate that the dimensions are valid numbers
        const validSize = Number.isFinite(newPlaneSize[0]) && Number.isFinite(newPlaneSize[1]) &&
            newPlaneSize[0] > 0 && newPlaneSize[1] > 0;
        
        const finalSize: [number, number] = validSize ? newPlaneSize : [1, 1];
        
        if (!validSize) {
            console.warn('SimpleText: Invalid plane size calculated', { newPlaneSize, canvasWidth, canvasHeight });
        }

        // Create a texture from the canvas
        // Dispose old texture to prevent memory leaks
        if (textureRef.current) {
            textureRef.current.dispose();
        }
        const texture = new THREE.CanvasTexture(canvas);
        // The canvas is supersampled well past its on-screen size, so linear
        // minification resolves the extra samples instead of aliasing them the
        // way NearestFilter did. LinearFilter for magFilter keeps scaling up smooth.
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.generateMipmaps = false; // Avoid blurring from mipmaps
        textureRef.current = texture;

        // Keep the R3F-owned unit plane stable and size it through the mesh.
        // Updating React state here used to schedule a second render for every
        // texture refresh and could exceed React's nested-update limit while an
        // area label was changing rapidly during construction.
        if (textMesh.current) {
            const material = textMesh.current.material as THREE.MeshBasicMaterial | undefined;
            if (!material) return;

            textMesh.current.scale.set(finalSize[0], finalSize[1], 1);

            // Apply texture to material
            material.map = texture;
            material.transparent = true;
            material.depthTest = depthTest;
            material.depthWrite = depthTest;
            material.side = THREE.DoubleSide;
            material.needsUpdate = true;
            textMesh.current.renderOrder = renderOrder;
            invalidate();
        }
    }, [children, fontSize, fontWeight, color, padding, renderOrder, depthTest, font, renderStore.rendering]);

    // Cleanup texture on unmount
    useEffect(() => {
        return () => {
            if (textureRef.current) {
                textureRef.current.dispose();
            }
        };
    }, []);

    return (
        <mesh ref={textMesh} position={position as [number, number, number]} rotation={new THREE.Euler(...rotation)} renderOrder={renderOrder} raycast={raycast}>
            <planeGeometry args={[1, 1]} />
            <meshBasicMaterial transparent depthTest={depthTest} depthWrite={depthTest} side={THREE.DoubleSide} />
        </mesh>
    );
});
